import { useState } from 'react';
import { searchRelations, SearchParams, SearchResult, searchSingles, SingleSearchParams, SingleSearchResult } from '../services/searchService';

// Cache global pour stocker temporairement les données
const globalCache: Record<string, any> = {};

// Durée de cache très courte pour les résultats de recherche (5 secondes)
const SEARCH_CACHE_DURATION = 5 * 1000;

// Fonction pour générer une clé de cache unique pour chaque recherche
const generateSearchCacheKey = (params: SearchParams | SingleSearchParams, prefix: string = 'search'): string => {
  const sortedParams = Object.entries(params)
    .filter(([_, value]) => value !== undefined && value !== '')
    .sort(([a], [b]) => a.localeCompare(b));
  
  // Ajouter un timestamp pour forcer le rafraîchissement plus fréquent
  const timestamp = Math.floor(Date.now() / SEARCH_CACHE_DURATION);
  return `${prefix}_${timestamp}_${sortedParams.map(([key, value]) => `${key}_${value}`).join('_')}`;
};

export function useSearch() {
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [results, setResults] = useState<SearchResult[]>([]);

  const search = async (params: SearchParams) => {
    try {
      setIsSearching(true);
      setError(null);

      // Ne pas utiliser le cache pour toujours avoir les noms actuels des créateurs
      console.log('Exécution de la recherche directement sans utiliser le cache');
      
      // Si pas de cache valide, faire la recherche
      const searchResults = await searchRelations(params);
      
      // S'assurer que tous les résultats ont match_percentage comme nombre
      const validResults = searchResults.map(result => ({
        ...result,
        match_percentage: typeof result.match_percentage === 'number' 
          ? result.match_percentage 
          : Number(result.match_percentage) || 0,
        created_at: result.created_at || new Date().toISOString(),
        comment: result.comment || null
      }));
      
      setResults(validResults);
      return validResults;
    } catch (err) {
      console.error('Error in useSearch:', err);
      setError(err instanceof Error ? err.message : 'Une erreur est survenue');
      return [];
    } finally {
      setIsSearching(false);
    }
  };

  // Fonction pour vider le cache de recherche
  const clearSearchCache = () => {
    // Supprimer tous les caches de recherche
    Object.keys(sessionStorage).forEach(key => {
      if (key.startsWith('search_')) {
        sessionStorage.removeItem(key);
      }
    });
    
    // Vider aussi le cache global des profils
    Object.keys(globalCache).forEach(key => {
      if (key.includes('profile') || key.includes('search')) {
        delete globalCache[key];
      }
    });
  };

  return {
    search,
    isSearching,
    error,
    results,
    clearSearchCache,
  };
}

export function useSingleSearch() {
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [results, setResults] = useState<SingleSearchResult[]>([]);

  const search = async (params: SingleSearchParams) => {
    try {
      setIsSearching(true);
      setError(null);

      // Générer la clé de cache pour cette recherche
      const cacheKey = generateSearchCacheKey(params, 'single_search');

      // Vérifier si nous avons des résultats en cache
      const cachedResults = sessionStorage.getItem(cacheKey);
      if (cachedResults) {
        const { data, timestamp } = JSON.parse(cachedResults);
        // Vérifier si le cache est encore valide (moins de 30 secondes)
        if (Date.now() - timestamp < SEARCH_CACHE_DURATION) {
          console.log('Utilisation des résultats en cache');
          setResults(data);
          return data;
        }
        // Si le cache est expiré, le supprimer
        sessionStorage.removeItem(cacheKey);
      }

      // Si pas de cache valide, faire la recherche
      const searchResults = await searchSingles(params);
      
      // Mettre en cache les résultats
      try {
        sessionStorage.setItem(cacheKey, JSON.stringify({
          data: searchResults,
          timestamp: Date.now()
        }));
      } catch (cacheError) {
        // Si le stockage échoue (ex: quota dépassé), simplement continuer
        console.warn('Impossible de mettre en cache les résultats:', cacheError);
      }
      
      setResults(searchResults);
      return searchResults;
    } catch (err) {
      console.error('Error in useSingleSearch:', err);
      setError(err instanceof Error ? err.message : 'Une erreur est survenue');
      return [];
    } finally {
      setIsSearching(false);
    }
  };

  // Fonction pour vider le cache de recherche
  const clearSearchCache = () => {
    // Supprimer uniquement les entrées de cache liées à la recherche de célibataires
    Object.keys(sessionStorage).forEach(key => {
      if (key.startsWith('single_search_')) {
        sessionStorage.removeItem(key);
      }
    });
  };

  return {
    search,
    isSearching,
    error,
    results,
    clearSearchCache,
  };
} 